import { Link } from 'react-router-dom';
import { MarketingShell } from '../components/MarketingShell';

// Public-facing explainer for Exam Mode. Linked from the marketing footer, the
// pricing page, the Exam Mode intro consent screen, and from any school's
// access-arrangements documentation we hand over.
export function ExamModeInfoScreen() {
  return (
    <MarketingShell>
      <div className="max-w-3xl mx-auto px-6 py-12">
        <p className="uppercase tracking-[0.2em] text-xs text-accent font-semibold">
          For schools and centres
        </p>
        <h1 className="mt-3 text-4xl sm:text-5xl font-semibold tracking-tight">
          Exam Mode
        </h1>
        <p className="mt-4 text-ink/65 leading-relaxed">
          A locked attempt mode designed to align with JCQ access arrangements,
          so candidates who already use My Dyslexia Tools can keep using it in real exams.
        </p>

        <section className="mt-10 bg-white border border-ink/10 rounded-2xl p-6">
          <h2 className="text-xl font-semibold tracking-tight">What Exam Mode disables</h2>
          <ul className="mt-3 space-y-2 text-ink/80 leading-relaxed list-disc list-inside">
            <li>AI question detection. Candidates tap the answer slot themselves.</li>
            <li>The "Simpler" button.</li>
            <li>Word prediction in the typed-answer flow.</li>
            <li>Auto-correction of dictated speech against the worksheet text.</li>
            <li>Premium neural voices (basic Read Aloud only - the same audio quality available to any device).</li>
            <li>Settings, History, and Tools navigation.</li>
            <li>Any other AI-assisted feature added later.</li>
          </ul>
        </section>

        <section className="mt-6 bg-white border border-ink/10 rounded-2xl p-6">
          <h2 className="text-xl font-semibold tracking-tight">What Exam Mode keeps</h2>
          <ul className="mt-3 space-y-2 text-ink/80 leading-relaxed list-disc list-inside">
            <li>Photo or upload capture of the paper.</li>
            <li>Read Aloud of each question, on demand, via the device's standard text-to-speech.</li>
            <li>Dictation (speech-to-text) for the candidate's answers.</li>
            <li>Typing as an alternative input.</li>
            <li>Saving and exporting the completed paper as PDF.</li>
            <li>An append-only audit trail of every action taken.</li>
          </ul>
        </section>

        <section className="mt-6 bg-warm/60 border border-amber-300 rounded-2xl p-6">
          <h2 className="text-xl font-semibold tracking-tight">The audit log</h2>
          <p className="mt-2 text-ink/80 leading-relaxed">
            Every meaningful action inside an exam attempt is recorded with a server-side
            timestamp. The log includes: attempt start and end, end reason, each question
            displayed, each read-aloud, each saved answer, and any attempt to access a blocked
            feature.
          </p>
          <p className="mt-3 text-ink/80 leading-relaxed">
            The log is append-only. Once written, entries cannot be edited or deleted, including
            by Creative Sauce. Schools can request a copy of any attempt's audit log by emailing{' '}
            <a className="text-accent underline" href="mailto:support@mydyslexiatools.com">
              support@mydyslexiatools.com
            </a>{' '}
            with the candidate number and paper title.
          </p>
        </section>

        <section className="mt-6 bg-white border border-ink/10 rounded-2xl p-6">
          <h2 className="text-xl font-semibold tracking-tight">JCQ alignment</h2>
          <p className="mt-2 text-ink/80 leading-relaxed">
            Exam Mode is designed to align with the JCQ rules for computer reader / scribe access
            arrangements as published in the most recent guidance. No internet lookups, no
            spell-check, no predictive text, no dictionary, no translation, no AI rephrasing.
            We are in the process of formally registering for the relevant approvals. In the
            meantime, the candidate's centre is responsible for confirming the arrangement is
            permitted for the specific paper being sat.
          </p>
        </section>

        <section className="mt-6 bg-white border border-ink/10 rounded-2xl p-6">
          <h2 className="text-xl font-semibold tracking-tight">For schools</h2>
          <p className="mt-2 text-ink/80 leading-relaxed">
            We are taking expressions of interest from schools who would like to pilot My Dyslexia
            Tools for a candidate cohort. If that's you, email{' '}
            <a className="text-accent underline" href="mailto:support@mydyslexiatools.com">
              support@mydyslexiatools.com
            </a>{' '}
            with the number of candidates and the term you have in mind.
          </p>
        </section>

        <div className="mt-10 text-center">
          <Link
            to="/app/exam/start"
            className="inline-flex items-center justify-center bg-accent text-white px-7 py-3 rounded-full font-medium hover:brightness-105 transition"
          >
            Start an exam attempt
          </Link>
          <p className="mt-3 text-xs text-ink/45">Requires sign-in.</p>
        </div>
      </div>
    </MarketingShell>
  );
}
